import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { NAV_LINKS, DESKTOP_NAV } from '../../data/content';
import { scrollToId } from '../../hooks/useLenis';
import logo from '../../assets/logo.png';

const Wordmark = ({ onClick, className = 'h-11 sm:h-14' }) => (
  <button
    onClick={onClick}
    data-testid="nav-logo"
    className="group flex items-center"
    aria-label="London Biotech International — home"
  >
    <img
      src={logo}
      alt="London Biotech International Ltd"
      className={`${className} w-auto transition-opacity duration-300 group-hover:opacity-90`}
    />
  </button>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-navy-950/80 backdrop-blur-xl border-b border-white/10'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8 py-4">
          <Wordmark onClick={() => go('home')} />

          <div className="hidden lg:flex items-center gap-8">
            {DESKTOP_NAV.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="relative text-[13px] font-medium tracking-wide text-white/70 transition-colors hover:text-white after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-300 hover:after:w-full"
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-get-in-touch"
              onClick={() => go('contact')}
              className="hidden sm:inline-flex items-center border border-gold px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-navy-950"
            >
              Get in Touch
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              className="flex h-10 w-10 items-center justify-center border border-white/15 text-white lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-navy-950/98 backdrop-blur-xl"
          >
            <div className="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-white/10">
              <Wordmark onClick={() => go('home')} />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center border border-white/15 text-white"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-col px-5 sm:px-8 py-8 gap-1 overflow-y-auto max-h-[calc(100vh-72px)]">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(l.id)}
                  className="flex items-baseline gap-4 border-b border-white/5 py-4 text-left"
                >
                  <span className="font-serif text-xs text-gold/70">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="font-serif text-2xl text-white">{l.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
